import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import AddressSuggestInput from '../components/AddressSuggestInput';
import PageActions from '../components/PageActions';
import ActivityTypeSelect from '../components/ActivityTypeSelect';
import TripMap from '../components/TripMap';
import UnescoSuggestionBanner from '../components/UnescoSuggestionBanner';
import { apiUrl } from '../services/api';
import DescriptionDisplay from '../components/DescriptionDisplay';
import { buildGoogleMapsUrl, formatAddressSummary } from '../utils/formatters';
import { getDatesInRange, formatDateReadable, formatTime24, formatDateLocal, formatWeekday } from '../utils/dateTime';
import { sortByDate } from '../utils/sort';
import { requireFields } from '../utils/validation';

/** Sensor that does not activate on inputs/textareas/buttons or Google Places dropdown so typing in forms works. */
function isInteractiveElement(el) {
  if (!el || typeof el.closest !== 'function') return false;
  if (el.closest?.('.pac-container')) return true; // Google Places Autocomplete dropdown
  if (el.closest?.('[data-no-dnd]')) return true; // Explicit exclude
  const interactive = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'];
  let node = el;
  while (node && node !== document.body) {
    const tag = (node.tagName || '').toUpperCase();
    if (interactive.includes(tag)) return true;
    if (node.getAttribute?.('contenteditable') === 'true') return true;
    node = node.parentElement;
  }
  return false;
}

class NonInputPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }) => {
        if (isInteractiveElement(event?.target)) return false;
        if (!event?.isPrimary || event?.button !== 0) return false;
        return true;
      },
    },
  ];
}

const API = apiUrl;

function Wishlist() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [tripName, setTripName] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [lodgings, setLodgings] = useState([]);
  const [activities, setActivities] = useState([]);
  const [sunEvents, setSunEvents] = useState([]);
  const [items, setItems] = useState([]);
  const [activityCategories, setActivityCategories] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);

  const [isAdding, setIsAdding] = useState(false);
  const [newDraft, setNewDraft] = useState({
    title: '',
    description: '',
    category_id: '',
    type_id: '',
    address: '',
    notes: '',
    url: '',
    start_time: '',
    end_time: '',
    destination_id: '',
  });
  const [addressPlace, setAddressPlace] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [editAddressPlace, setEditAddressPlace] = useState(null);
  const [unescoSuggestion, setUnescoSuggestion] = useState(null); // { ideaId, suggestion }

  const [clips, setClips] = useState([]);
  const [focusedIdeaId, setFocusedIdeaId] = useState(null);
  const [categoryFilterId, setCategoryFilterId] = useState('');
  const [researchDestId, setResearchDestId] = useState('');
  const [itineraryDestId, setItineraryDestId] = useState('');
  const [promoteId, setPromoteId] = useState(null);
  const [promoteDest, setPromoteDest] = useState('');
  const [promoteDate, setPromoteDate] = useState('');
  const [promoteTime, setPromoteTime] = useState('12:00');
  const [isPromoting, setIsPromoting] = useState(false);

  const sensors = useSensors(
    useSensor(NonInputPointerSensor, { activationConstraint: { delay: 100, tolerance: 8 } })
  );

  const loadData = useCallback(() => {
    if (!tripId) return;
    fetch(API('/api/trips'))
      .then(res => res.json())
      .then(trips => {
        const t = (trips || []).find(x => String(x.trip_id) === String(tripId));
        if (t) setTripName(t.trip_name);
      })
      .catch(() => {});
    fetch(API(`/api/trips/${tripId}/destinations`))
      .then(res => res.json())
      .then(setDestinations)
      .catch(() => setDestinations([]));
    fetch(API(`/api/trips/${tripId}/lodgings`))
      .then(res => res.ok ? res.json() : [])
      .then(setLodgings)
      .catch(() => setLodgings([]));
    fetch(API(`/api/trips/${tripId}/activities`))
      .then(res => res.ok ? res.json() : [])
      .then(setActivities)
      .catch(() => setActivities([]));
    fetch(API(`/api/trips/${tripId}/sun_events`))
      .then(res => res.ok ? res.json() : [])
      .then(setSunEvents)
      .catch(() => setSunEvents([]));
    fetch(API(`/api/trips/${tripId}/wishlist`))
      .then(res => res.json())
      .then(setItems)
      .catch(() => setItems([]));
    fetch(API(`/api/trips/${tripId}/clips`))
      .then(res => res.ok ? res.json() : [])
      .then(setClips)
      .catch(() => setClips([]));
    fetch(API('/api/activity_categories'))
      .then(res => res.ok ? res.json() : [])
      .then(setActivityCategories)
      .catch(() => setActivityCategories([]));
    fetch(API('/api/activity_types'))
      .then(res => res.ok ? res.json() : [])
      .then(setActivityTypes)
      .catch(() => setActivityTypes([]));
  }, [tripId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (destinations?.length && !researchDestId) {
      setResearchDestId(String(destinations[0].destination_id));
    }
  }, [destinations, researchDestId]);

  useEffect(() => {
    if (destinations?.length && !itineraryDestId) {
      setItineraryDestId(String(destinations[0].destination_id));
    }
  }, [destinations, itineraryDestId]);

  useEffect(() => {
    if (researchDestId && researchDestId !== '__overview__') {
      setItineraryDestId(researchDestId);
    }
  }, [researchDestId]);


  const typeIdToType = useMemo(
    () => new Map((activityTypes || []).map(t => [String(t.type_id), t])),
    [activityTypes]
  );
  const typeToCategoryId = useMemo(
    () => new Map((activityTypes || []).map(t => [String(t.type_id), String(t.category_id)])),
    [activityTypes]
  );
  const categoryIdToName = useMemo(
    () => new Map((activityCategories || []).map(c => [String(c.category_id), c.category_name || ''])),
    [activityCategories]
  );

  const itemsByDestination = useMemo(() => {
    const destOrder = sortByDate(destinations, 'start_date');
    const groups = [];
    destOrder.forEach(d => {
      const destItems = (items || []).filter(i => String(i.destination_id || '') === String(d.destination_id));
      groups.push({ dest: d, items: destItems, key: String(d.destination_id) });
    });
    const unassigned = (items || []).filter(i => !i.destination_id);
    groups.push({ dest: null, items: unassigned, key: '__unassigned__' });
    return groups;
  }, [items, destinations]);

  const effectiveResearchDestId = researchDestId || (destinations?.[0] ? String(destinations[0].destination_id) : '__overview__');
  const isDestinationView = effectiveResearchDestId && effectiveResearchDestId !== '__overview__';
  const selectedDest = (destinations || []).find(d => String(d.destination_id) === String(effectiveResearchDestId));
  const displayedGroups = useMemo(() => {
    let groups = itemsByDestination;
    if (isDestinationView) {
      const destGroup = itemsByDestination.find(g => g.key === effectiveResearchDestId);
      const unassignedGroup = itemsByDestination.find(g => g.key === '__unassigned__');
      groups = [];
      if (destGroup) groups.push(destGroup);
      if (unassignedGroup?.items?.length) groups.push(unassignedGroup);
    }
    if (!categoryFilterId) return groups;
    return groups.map(g => ({
      ...g,
      items: (g.items || []).filter(item => {
        const catId = item.type_id ? typeToCategoryId.get(String(item.type_id)) : null;
        return catId && String(catId) === String(categoryFilterId);
      }),
    }));
  }, [itemsByDestination, isDestinationView, effectiveResearchDestId, categoryFilterId, typeToCategoryId]);

  const openAddForm = useCallback((destinationId = '') => {
    setIsAdding(true);
    setNewDraft(prev => ({ ...prev, destination_id: destinationId }));
  }, []);

  const itineraryByDestination = useMemo(() => {
    const destById = new Map((destinations || []).map(d => [String(d.destination_id), d]));
    const sunByDestDate = new Map();
    (sunEvents || []).forEach(se => {
      sunByDestDate.set(`${se.destination_id}:${se.date}`, se);
    });
    const actsByDestDate = {};
    (activities || [])
      .filter(a => a.destination_id)
      .forEach(a => {
        const dest = destById.get(String(a.destination_id));
        if (!dest) return;
        let dateStr = formatDateLocal(a.start_time || a.end_time, dest.timezone).slice(0, 10);
        if (!dateStr) dateStr = String(dest.start_date || '').slice(0, 10);
        if (!dateStr) return;
        const key = String(a.destination_id);
        if (!actsByDestDate[key]) actsByDestDate[key] = {};
        if (!actsByDestDate[key][dateStr]) actsByDestDate[key][dateStr] = [];
        actsByDestDate[key][dateStr].push(a);
      });
    return (destinations || [])
      .slice()
      .sort((a, b) => String(a.start_date || '').localeCompare(String(b.start_date || '')))
      .map(dest => {
        const dates = getDatesInRange(dest.start_date, dest.end_date);
        const days = dates.map(dateStr => {
          const acts = (actsByDestDate[String(dest.destination_id)] || {})[dateStr] || [];
          acts.sort((x, y) => {
            const xs = x.start_time || '';
            const ys = y.start_time || '';
            return xs.localeCompare(ys) || (x.sort_order || 0) - (y.sort_order || 0);
          });
          const sun = sunByDestDate.get(`${dest.destination_id}:${dateStr}`);
          const sunrise = sun?.events?.find(e => e.kind === 'sunrise')?.time;
          const sunset = sun?.events?.find(e => e.kind === 'sunset')?.time;
          return { dateStr, acts, sunrise, sunset };
        });
        return { dest, days };
      });
  }, [destinations, activities, sunEvents]);

  const activityKeysInItinerary = useMemo(() => {
    const set = new Set();
    (activities || []).forEach(a => {
      const t = (a.title || '').trim();
      const tid = a.type_id || '';
      if (t) set.add(`${t}__${tid}`);
    });
    return set;
  }, [activities]);

  const addItem = () => {
    const title = (newDraft.title || '').trim();
    if (!requireFields([[title, 'Title']])) return;
    const payload = {
      title,
      description: (newDraft.description || '').trim() || undefined,
      type_id: (newDraft.type_id || '').trim() || undefined,
      destination_id: (newDraft.destination_id || '').trim() || undefined,
      address: addressPlace?.display_name || (newDraft.address || '').trim() || undefined,
      lat: addressPlace?.lat,
      lon: addressPlace?.lon,
      notes: (newDraft.notes || '').trim() || undefined,
      url: (newDraft.url || '').trim() || undefined,
      start_time: (newDraft.start_time || '').trim() || undefined,
      end_time: (newDraft.end_time || '').trim() || undefined,
    };
    fetch(API(`/api/trips/${tripId}/wishlist`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || (data.detail || `Server error ${res.status}`) || 'Request failed');
        }
        return data;
      })
      .then((data) => {
        if (data.error) {
          window.alert(data.error);
          return;
        }
        setIsAdding(false);
        setNewDraft({ title: '', description: '', category_id: '', type_id: '', address: '', notes: '', url: '', start_time: '', end_time: '', destination_id: newDraft.destination_id || '' });
        setAddressPlace(null);
        if (data.unesco_suggestion) {
          setUnescoSuggestion({ ideaId: data.idea_id, suggestion: data.unesco_suggestion });
        }
        loadData();
      })
      .catch((err) => window.alert(err.message || 'Failed to add item'));
  };

  const updateItem = (ideaId) => {
    const title = (editDraft.title || '').trim();
    if (!requireFields([[title, 'Title']])) return;
    const payload = {
      title,
      description: (editDraft.description || '').trim() || undefined,
      type_id: (editDraft.type_id || '').trim() || undefined,
      destination_id: (editDraft.destination_id || '').trim() || undefined,
      address: editAddressPlace?.display_name || (editDraft.address || '').trim() || undefined,
      lat: editAddressPlace?.lat,
      lon: editAddressPlace?.lon,
      notes: (editDraft.notes || '').trim() || undefined,
      url: (editDraft.url || '').trim() || undefined,
      start_time: (editDraft.start_time || '').trim() || undefined,
      end_time: (editDraft.end_time || '').trim() || undefined,
    };
    fetch(API(`/api/trips/${tripId}/wishlist/${ideaId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(data => {
        if (data?.error) {
          window.alert(data.error);
          return;
        }
        setEditingId(null);
        setEditDraft({});
        setEditAddressPlace(null);
        loadData();
      })
      .catch(() => window.alert('Failed to update'));
  };

  const deleteItem = (ideaId) => {
    if (!window.confirm('Remove this from your research?')) return;
    fetch(API(`/api/trips/${tripId}/wishlist/${ideaId}`), { method: 'DELETE' })
      .then(() => loadData())
      .catch(() => window.alert('Failed to remove'));
  };

  const demoteActivity = useCallback((activityId) => {
    fetch(API(`/api/trips/${tripId}/activities/${activityId}/demote`), { method: 'POST' })
      .then(res => res.json())
      .then(data => {
        if (data?.error) window.alert(data.error);
        else loadData();
      })
      .catch(() => window.alert('Failed to move back to Saved'));
  }, [tripId, loadData]);

  const rescheduleActivity = useCallback((activity, destId, dateStr) => {
    const startHHMM = formatTime24(activity.start_time, (destinations || []).find(d => String(d.destination_id) === String(destId))?.timezone) || null;
    const endHHMM = formatTime24(activity.end_time, (destinations || []).find(d => String(d.destination_id) === String(destId))?.timezone) || null;
    const payload = {
      destination_id: destId,
      start_date: dateStr,
      ...(startHHMM && { start_time: startHHMM }),
      ...(endHHMM && { end_time: endHHMM }),
    };
    return fetch(API(`/api/trips/${tripId}/activities/${activity.activity_id}/reschedule`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(data => {
        if (data?.error) window.alert(data.error);
        else loadData();
      })
      .catch(() => window.alert('Failed to reschedule'));
  }, [tripId, destinations, loadData]);

  const promoteItemDirect = useCallback((ideaId, destId, dateStr, startTime = '12:00') => {
    if (!ideaId || !destId || !dateStr) return;
    setIsPromoting(true);
    const payload = { destination_id: destId, start_date: dateStr, start_time: startTime || undefined };
    fetch(API(`/api/trips/${tripId}/wishlist/${ideaId}/promote`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(data => {
        if (data?.error) window.alert(data.error);
        else loadData();
      })
      .catch(() => window.alert('Failed to promote'))
      .finally(() => setIsPromoting(false));
  }, [tripId, loadData]);

  const promoteItem = () => {
    if (!promoteId) return;
    const destId = (promoteDest || '').trim();
    const date = (promoteDate || '').trim();
    if (!destId || !date) {
      window.alert('Please select destination and date.');
      return;
    }
    setIsPromoting(true);
    const payload = {
      destination_id: destId,
      start_date: date,
      start_time: (promoteTime || '').trim() || undefined,
    };
    fetch(API(`/api/trips/${tripId}/wishlist/${promoteId}/promote`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(data => {
        if (data?.error) {
          window.alert(data.error);
          return;
        }
        setPromoteId(null);
        setPromoteDest('');
        setPromoteDate('');
        setPromoteTime('12:00');
        loadData();
        navigate(`/itinerary/${tripId}`);
      })
      .catch(() => window.alert('Failed to promote'))
      .finally(() => setIsPromoting(false));
  };

  const promoteDates = promoteDest
    ? (() => {
        const d = (destinations || []).find(x => String(x.destination_id) === String(promoteDest));
        return d ? getDatesInRange(d.start_date, d.end_date) : [];
      })()
    : [];

  const startEdit = (item) => {
    setEditingId(item.idea_id);
    setEditDraft({
      title: item.title,
      description: item.description || '',
      category_id: item.type_id ? (typeIdToType.get(String(item.type_id))?.category_id || '') : '',
      type_id: item.type_id || '',
      address: item.address || '',
      notes: item.notes || '',
      url: item.url || '',
      start_time: item.start_time || '',
      end_time: item.end_time || '',
      destination_id: item.destination_id || '',
    });
    setEditAddressPlace(
      item.address && (item.lat != null || item.lon != null)
        ? { display_name: item.address, lat: item.lat, lon: item.lon }
        : null
    );
  };

  const startPromote = (item) => {
    setPromoteId(item.idea_id);
    setPromoteDest(item.destination_id ? String(item.destination_id) : '');
    setPromoteDate('');
    setPromoteTime('12:00');
  };

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over) return;
    const overId = String(over.id);
    const data = active.data?.current;
    if (data?.type === 'idea' && overId.startsWith('day__')) {
      const [, destId, dateStr] = overId.split('__');
      if (destId && dateStr) promoteItemDirect(data.item.idea_id, destId, dateStr, data.item.start_time);
    } else if (data?.type === 'activity') {
      if (overId === 'ideas-zone') {
        demoteActivity(data.activity.activity_id);
      } else if (overId.startsWith('day__')) {
        const [, destId, dateStr] = overId.split('__');
        if (destId && dateStr) {
          const act = data.activity;
          const sameDest = String(act.destination_id) === String(destId);
          const actDest = (destinations || []).find(d => String(d.destination_id) === String(act.destination_id));
          const currentDate = actDest?.timezone ? formatDateLocal(act.start_time || act.end_time, actDest.timezone).slice(0, 10) : '';
          if (!sameDest || currentDate !== dateStr) {
            rescheduleActivity(act, destId, dateStr);
          }
        }
      }
    }
  }, [promoteItemDirect, demoteActivity, rescheduleActivity, destinations]);

  const DraggableIdeaCard = ({ item, isEditing, isInItinerary, children }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: `idea-${item.idea_id}`,
      data: { type: 'idea', item },
      disabled: isEditing,
    });
    const hasCoords = Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lon));
    if (isEditing) {
      return (
        <li className="tp-card tp-card--tight" style={{ marginBottom: 12 }}>
          {children}
        </li>
      );
    }
    const isFocused = hasCoords && String(focusedIdeaId) === String(item.idea_id);
    return (
      <li
        ref={setNodeRef}
        className="tp-card tp-card--tight"
        style={{
          marginBottom: 12,
          opacity: isDragging ? 0.5 : 1,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          cursor: hasCoords ? 'pointer' : undefined,
          ...(isFocused ? { borderColor: 'var(--tp-accent)', boxShadow: '0 0 0 1px var(--tp-accent)' } : {}),
        }}
      >
        <div
          {...listeners}
          {...attributes}
          style={{
            flexShrink: 0,
            width: 24,
            padding: '4px 0',
            cursor: 'grab',
            color: 'var(--tp-muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '-4px 0',
          }}
          title="Drag to move"
          aria-label="Drag to move to itinerary"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}>
            <circle cx="9" cy="6" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="9" cy="18" r="1.5" />
            <circle cx="15" cy="6" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="15" cy="18" r="1.5" />
          </svg>
        </div>
        {isInItinerary && (
          <div
            style={{
              flexShrink: 0,
              width: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--tp-success, #22c55e)',
            }}
            title="In itinerary"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        )}
        <div
          style={{ flex: 1, minWidth: 0 }}
          onClick={hasCoords ? () => setFocusedIdeaId(prev => (String(prev) === String(item.idea_id) ? null : item.idea_id)) : undefined}
          role={hasCoords ? 'button' : undefined}
          tabIndex={hasCoords ? 0 : undefined}
          onKeyDown={hasCoords ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFocusedIdeaId(prev => (String(prev) === String(item.idea_id) ? null : item.idea_id)); } } : undefined}
        >
          {children}
        </div>
      </li>
    );
  };

  const DraggableActivityBlock = ({ activity, dest, children }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: `activity-${activity.activity_id}`,
      data: { type: 'activity', activity },
    });
    return (
      <div
        ref={setNodeRef}
        style={{
          fontSize: 12,
          padding: '6px 4px 6px 0',
          background: 'var(--tp-surface-2)',
          borderRadius: 8,
          border: '1px solid var(--tp-border)',
          opacity: isDragging ? 0.5 : 1,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        <div
          {...listeners}
          {...attributes}
          style={{
            flexShrink: 0,
            width: 20,
            padding: '2px 0',
            cursor: 'grab',
            color: 'var(--tp-muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Drag to move"
          aria-label="Drag to reorder"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}>
            <circle cx="9" cy="6" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="9" cy="18" r="1.5" />
            <circle cx="15" cy="6" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="15" cy="18" r="1.5" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>{children}</div>
      </div>
    );
  };

  const DroppableIdeasZone = ({ children }) => {
    const { isOver, setNodeRef } = useDroppable({ id: 'ideas-zone' });
    return (
      <div
        ref={setNodeRef}
        style={{
          minHeight: 80,
          borderRadius: 8,
          background: isOver ? 'var(--tp-accent-subtle, rgba(99, 102, 241, 0.1))' : 'transparent',
          border: isOver ? '2px dashed var(--tp-accent)' : '2px dashed transparent',
          transition: 'background 0.15s, border 0.15s',
        }}
      >
        {children}
      </div>
    );
  };

  const DroppableDayBox = ({ destId, dateStr, children }) => {
    const { isOver, setNodeRef } = useDroppable({
      id: `day__${destId}__${dateStr}`,
      data: { destId, dateStr },
    });
    return (
      <div
        ref={setNodeRef}
        style={{
          minHeight: 40,
          borderRadius: 8,
          background: isOver ? 'var(--tp-accent-subtle, rgba(99, 102, 241, 0.1))' : 'transparent',
          border: isOver ? '2px dashed var(--tp-accent)' : '2px dashed transparent',
          transition: 'background 0.15s, border 0.15s',
        }}
      >
        {children}
      </div>
    );
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
    <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
      <section className="tp-hero" style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
          <div>
            <p className="tp-hero__kicker">Research</p>
            <h1 className="tp-hero__title" style={{ marginBottom: 4 }}>
              {isDestinationView && selectedDest ? selectedDest.destination_name : tripName || 'Research'}
            </h1>
            {!isDestinationView && tripName && (
              <p style={{ fontSize: 14, color: 'var(--tp-muted)', margin: 0 }}>{tripName}</p>
            )}
          </div>
        </div>
        <PageActions
          actions={[
            { label: 'Back to Home', to: '/', key: 'home' },
            { label: 'Itinerary', to: `/itinerary/${tripId}`, key: 'itinerary' },
            { label: 'Inspiration', to: `/trips/${tripId}/inspiration`, key: 'inspiration' },
            { label: 'Trip Setup', to: `/trip-setup/${tripId}`, key: 'setup' },
          ]}
        />
      </section>

      {(() => {
        const hasValidCoords = (obj) => {
          const lat = obj?.lat;
          const lon = obj?.lon;
          if (lat == null || lon == null) return false;
          const nLat = Number(lat);
          const nLon = Number(lon);
          if (!Number.isFinite(nLat) || !Number.isFinite(nLon)) return false;
          if (nLat === 0 && nLon === 0) return false;
          return true;
        };
        let mapItems = (items || []).filter(hasValidCoords);
        if (isDestinationView && selectedDest) {
          mapItems = mapItems.filter(i => String(i.destination_id || '') === String(selectedDest.destination_id));
        }
        if (categoryFilterId) {
          mapItems = mapItems.filter(i => {
            const catId = i.type_id ? typeToCategoryId.get(String(i.type_id)) : null;
            return catId && String(catId) === String(categoryFilterId);
          });
        }
        let lodgingsForMap = (lodgings || []).filter(hasValidCoords);
        if (isDestinationView && selectedDest) {
          lodgingsForMap = lodgingsForMap.filter(l => String(l.destination_id || '') === String(selectedDest.destination_id));
        }
        const toFitPoint = (p) => [Number(p.lat), Number(p.lon)];
        const fixLodgingCoords = (l) => {
          let lat = Number(l.lat);
          let lon = Number(l.lon);
          const addr = String(l.address || '').toLowerCase();
          const looksLikeSpain = /spain|barcelona|08018|madrid|valencia|seville/.test(addr) || addr.includes(', spain');
          if (Number.isFinite(lat) && Number.isFinite(lon) && looksLikeSpain && lat >= 35 && lat <= 44 && lon >= -5 && lon < 0) {
            lon = -lon;
          }
          return { lat, lon };
        };
        const ideaPoints = mapItems.map(toFitPoint);
        const lodgingPoints = lodgingsForMap.map(l => {
          const { lat, lon } = fixLodgingCoords(l);
          return [lat, lon];
        });
        const allFitPoints = [...ideaPoints, ...lodgingPoints];
        const focusedItem = focusedIdeaId ? mapItems.find(i => String(i.idea_id) === String(focusedIdeaId)) : null;
        let mapFitPoints = focusedItem ? [toFitPoint(focusedItem)] : allFitPoints;

        const destHasCoords = selectedDest && Number.isFinite(Number(selectedDest.lat)) && Number.isFinite(Number(selectedDest.lon));
        const destCenter = destHasCoords ? [Number(selectedDest.lat), Number(selectedDest.lon)] : null;

        if (mapFitPoints.length === 0 && destCenter && isDestinationView) {
          mapFitPoints = [destCenter];
        }
        if (mapFitPoints.length === 0 && !isDestinationView) {
          const destPoints = (destinations || [])
            .filter(d => Number.isFinite(Number(d.lat)) && Number.isFinite(Number(d.lon)))
            .map(toFitPoint);
          if (destPoints.length > 0) mapFitPoints = destPoints;
        }

        const toMarker = (p, extras) => ({
          lat: Number(p.lat),
          lon: Number(p.lon),
          ...extras,
        });
        const ideaMarkers = mapItems.map(i => toMarker(i, { title: i.title, popup: i.address || undefined }));
        const lodgingMarkers = lodgingsForMap.map(l => {
          const { lat, lon } = fixLodgingCoords(l);
          return { lat, lon, title: l.lodging_name, popup: l.address || undefined };
        });
        const mapMarkers = [...ideaMarkers, ...lodgingMarkers];
        if (mapFitPoints.length === 0) {
          return (
            <section className="tp-section">
              <div className="tp-card" style={{ padding: 24, textAlign: 'center', color: 'var(--tp-muted)' }}>
                {isDestinationView && selectedDest
                  ? 'Add a location for this destination in Trip Setup to see it on the map.'
                  : 'Add locations for destinations in Trip Setup to see the map.'}
              </div>
              {(activityCategories?.length > 0 || destinations?.length > 0) && (
                <div
                  style={{
                    display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
                    marginTop: 12, padding: '12px 16px',
                    background: 'var(--tp-bg)', borderRadius: 12, border: '1px solid var(--tp-border)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                  }}
                >
                  <span style={{ fontSize: 13, color: 'var(--tp-muted)', marginRight: 4 }}>Filter</span>
                  {activityCategories?.length > 0 && (
                    <div className="tp-field" style={{ margin: 0, minWidth: 160 }}>
                      <select value={categoryFilterId} onChange={e => setCategoryFilterId(e.target.value)} style={{ fontSize: 14 }} title="Filter by category">
                        <option value="">All categories</option>
                        {(activityCategories || []).filter(c => c.category_name).sort((a, b) => String(a.category_name || '').localeCompare(String(b.category_name || ''))).map(c => (
                          <option key={c.category_id} value={String(c.category_id)}>{c.category_name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {destinations?.length > 0 && (
                    <div className="tp-field" style={{ margin: 0, minWidth: 180 }}>
                      <select value={effectiveResearchDestId} onChange={e => setResearchDestId(e.target.value)} style={{ fontSize: 14 }}>
                        {(destinations || []).slice().sort((a, b) => String(a.start_date || '').localeCompare(String(b.start_date || ''))).map(d => (
                          <option key={d.destination_id} value={String(d.destination_id)}>{d.destination_name}</option>
                        ))}
                        <option value="__overview__">Trip overview</option>
                      </select>
                    </div>
                  )}
                  {(() => {
                    const destClipCount = effectiveResearchDestId && effectiveResearchDestId !== '__overview__'
                      ? clips.filter(c => c.destinations.some(d => d.destination_id === effectiveResearchDestId)).length
                      : clips.length;
                    if (!destClipCount) return null;
                    const inspUrl = `/trips/${tripId}/inspiration`;
                    return (
                      <Link
                        to={inspUrl}
                        style={{ fontSize: 12, color: 'var(--tp-accent)', whiteSpace: 'nowrap', textDecoration: 'none' }}
                        title="View inspiration clips"
                      >
                        ▶ {destClipCount} clip{destClipCount !== 1 ? 's' : ''}
                      </Link>
                    );
                  })()}
                  <button
                    type="button"
                    className="tp-btn--icon"
                    aria-label={isAdding ? 'Cancel add' : 'Add idea'}
                    onClick={() => (isAdding ? setIsAdding(false) : openAddForm(isDestinationView ? effectiveResearchDestId : ''))}
                    style={{ marginLeft: 'auto' }}
                  >
                    +
                  </button>
                </div>
              )}
            </section>
          );
        }

        const fitMarkers = (mapItems.length === 0 && lodgingsForMap.length === 0)
          ? (destCenter
            ? [toMarker({ lat: destCenter[0], lon: destCenter[1] }, { title: selectedDest?.destination_name, popup: selectedDest?.display_name })]
            : (destinations || []).filter(d => Number.isFinite(Number(d.lat)) && Number.isFinite(Number(d.lon))).map(d => toMarker(d, { title: d.destination_name, popup: d.display_name })))
          : mapMarkers;

        return (
          <section
            className="tp-section"
            style={{
              position: 'sticky',
              top: 80,
              zIndex: 5,
              background: 'var(--tp-bg)',
              paddingBottom: 20,
              marginBottom: -20,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
            }}
          >
            <div className="tp-map tp-map--dark" style={{ height: 320, borderRadius: 16, overflow: 'hidden', position: 'relative' }}>
              <TripMap
                center={mapFitPoints[0]}
                zoom={mapItems.length === 0 ? 11 : 3}
                fitPoints={mapFitPoints}
                fitKey={`wishlist-${tripId}-${focusedIdeaId || 'all'}-${mapFitPoints.length}-${effectiveResearchDestId}`}
                fitMaxZoom={focusedItem ? 16 : 12}
                theme="dark"
                height={320}
                markers={fitMarkers}
              />
              {focusedItem && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 12,
                    left: 12,
                    right: 12,
                    background: 'rgba(11, 11, 11, 0.92)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid var(--tp-border)',
                    borderRadius: 12,
                    padding: '12px 14px',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{focusedItem.title}</div>
                    {focusedItem.address && (
                      <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 3 }}>
                        {formatAddressSummary(focusedItem.address)}
                      </div>
                    )}
                    {focusedItem.type_id && (
                      <div style={{ fontSize: 11, color: 'var(--tp-accent)', marginTop: 3 }}>
                        {categoryIdToName.get(typeToCategoryId.get(String(focusedItem.type_id)) || '')}
                        {' – '}
                        {typeIdToType.get(String(focusedItem.type_id))?.type_name || ''}
                      </div>
                    )}
                    {focusedItem.description && (
                      <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 4, maxHeight: 40, overflow: 'hidden' }}>
                        <DescriptionDisplay text={focusedItem.description} />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      {(() => {
                        const mapsQuery = focusedItem.address || focusedItem.title;
                        const mapsUrl = buildGoogleMapsUrl(mapsQuery);
                        if (!mapsUrl) return null;
                        return (
                          <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: 12,
                              color: '#8ab4f8',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              textDecoration: 'none',
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            Google Maps
                          </a>
                        );
                      })()}
                      {focusedItem.url && (
                        <a
                          href={focusedItem.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 12,
                            color: '#8ab4f8',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            textDecoration: 'none',
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                          Website
                        </a>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="tp-btn--icon"
                    style={{ flexShrink: 0, fontSize: 14, opacity: 0.6 }}
                    onClick={() => setFocusedIdeaId(null)}
                    aria-label="Close preview"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            {(mapItems.length === 0 && lodgingsForMap.length === 0) && (
              <p style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 8 }}>
                Add an address when creating or editing items to see them on the map.
              </p>
            )}
            {(activityCategories?.length > 0 || destinations?.length > 0) && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  alignItems: 'center',
                  marginTop: 12,
                  padding: '12px 16px',
                  background: 'var(--tp-bg)',
                  borderRadius: 12,
                  border: '1px solid var(--tp-border)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                  position: 'relative',
                  zIndex: 6,
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--tp-muted)', marginRight: 4 }}>Filter</span>
                {activityCategories?.length > 0 && (
                  <div className="tp-field" style={{ margin: 0, minWidth: 160 }}>
                    <select
                      value={categoryFilterId}
                      onChange={e => setCategoryFilterId(e.target.value)}
                      style={{ fontSize: 14 }}
                      title="Filter by category"
                    >
                      <option value="">All categories</option>
                      {(activityCategories || [])
                        .filter(c => c.category_name)
                        .sort((a, b) => String(a.category_name || '').localeCompare(String(b.category_name || '')))
                        .map(c => (
                          <option key={c.category_id} value={String(c.category_id)}>
                            {c.category_name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                {destinations?.length > 0 && (
                  <div className="tp-field" style={{ margin: 0, minWidth: 180 }}>
                    <select
                      value={effectiveResearchDestId}
                      onChange={e => setResearchDestId(e.target.value)}
                      style={{ fontSize: 14 }}
                    >
                      {(destinations || [])
                        .slice()
                        .sort((a, b) => String(a.start_date || '').localeCompare(String(b.start_date || '')))
                        .map(d => (
                          <option key={d.destination_id} value={String(d.destination_id)}>
                            {d.destination_name}
                          </option>
                        ))}
                      <option value="__overview__">Trip overview</option>
                    </select>
                  </div>
                )}
                <button
                  type="button"
                  className="tp-btn--icon"
                  aria-label={isAdding ? 'Cancel add' : 'Add idea'}
                  onClick={() => (isAdding ? setIsAdding(false) : openAddForm(isDestinationView ? effectiveResearchDestId : ''))}
                  style={{ marginLeft: 'auto' }}
                >
                  +
                </button>
              </div>
            )}
          </section>
        );
      })()}

      <section className="tp-section">
        <div className="tp-card">
          <h2 className="tp-section__title" style={{ margin: 0 }}>
            {isDestinationView && selectedDest ? selectedDest.destination_name : 'Saved'}
          </h2>

          {!destinations?.length && items.length === 0 ? (
            <DroppableIdeasZone>
              <p style={{ color: 'var(--tp-muted)', margin: '16px 0 0 0' }}>
                Add destinations in Trip Setup first, then use + in the filter bar to add research.
              </p>
            </DroppableIdeasZone>
          ) : (
            <DroppableIdeasZone>
            {displayedGroups.map(({ dest, items: groupItems, key: groupKey }) => {
              const showGroupHeader = !isDestinationView || groupKey === '__unassigned__';
              return (
              <div key={groupKey} style={{ marginBottom: 24 }}>
                {showGroupHeader && (
                <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--tp-muted)', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {dest ? dest.destination_name : 'Unassigned'}
                </h3>
                )}
                {(categoryFilterId && groupItems.length === 0) ? (
                  <p style={{ color: 'var(--tp-muted)', fontSize: 14, margin: '12px 0 0 0' }}>
                    No {categoryIdToName.get(String(categoryFilterId)) || 'items'} in {dest ? dest.destination_name : 'unassigned'}.
                  </p>
                ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {groupItems.map(item => (
                <DraggableIdeaCard
                  key={item.idea_id}
                  item={item}
                  isEditing={false}
                  isInItinerary={activityKeysInItinerary.has(`${(item.title || '').trim()}__${item.type_id || ''}`)}
                >
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 16 }}>{item.title}</div>
                          {(item.start_time || item.end_time) && (
                            <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>
                              {[item.start_time, item.end_time].filter(Boolean).join(' – ') || '—'}
                            </div>
                          )}
                          {item.description && (
                            <div style={{ color: 'var(--tp-muted)', marginTop: 4 }}>
                              <DescriptionDisplay text={item.description} />
                            </div>
                          )}
                          {(item.address || item.url) && (
                            <div style={{ color: 'var(--tp-muted)', fontSize: 13, marginTop: 4 }}>
                              {item.address}
                              {item.address && item.url && ' · '}
                              {item.url && (
                                <a href={item.url} target="_blank" rel="noopener noreferrer">Link</a>
                              )}
                            </div>
                          )}
                          {item.type_id && (
                            <div style={{ fontSize: 12, color: 'var(--tp-accent)', marginTop: 4 }}>
                              {categoryIdToName.get(typeToCategoryId.get(String(item.type_id)) || '')} – {typeIdToType.get(String(item.type_id))?.type_name || ''}
                            </div>
                          )}
                        </div>
                        <div className="tp-row" style={{ flexShrink: 0, gap: 8 }}>
                          <button
                            type="button"
                            className="tp-btn--ghost"
                            onClick={e => { e.stopPropagation(); startPromote(item); }}
                          >
                            Promote
                          </button>
                          <button
                            type="button"
                            className="tp-btn--ghost"
                            onClick={e => { e.stopPropagation(); startEdit(item); }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="tp-btn--danger"
                            onClick={e => { e.stopPropagation(); deleteItem(item.idea_id); }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </>
                </DraggableIdeaCard>
              ))}
                </ul>
                )}
              </div>
            );
            })}
            </DroppableIdeasZone>
          )}
        </div>
      </section>

      </div>

      <aside
        className="tp-card"
        style={{
          width: 280,
          flexShrink: 0,
          position: 'sticky',
          top: 80,
          alignSelf: 'flex-start',
          maxHeight: 'calc(100vh - 96px)',
          overflowY: 'auto',
        }}
      >
        <h3 className="tp-section__title" style={{ margin: '0 0 4px 0', fontSize: 14 }}>
          {isDestinationView && selectedDest ? selectedDest.destination_name : 'Itinerary'}
        </h3>
        {itineraryByDestination.length > 0 && (
          <p style={{ color: 'var(--tp-muted)', margin: '0 0 12px 0', fontSize: 11 }}>Drag between dates to reschedule · Drag to Saved to move back</p>
        )}
        {itineraryByDestination.length === 0 ? (
          <p style={{ color: 'var(--tp-muted)', margin: 0, fontSize: 13 }}>
            No destinations yet.{' '}
            <Link to={`/trip-setup/${tripId}`} style={{ color: 'var(--tp-accent)' }}>Add in Trip Setup</Link>
          </p>
        ) : itineraryByDestination.length > 1 && !isDestinationView ? (
          <>
            <div className="tp-field" style={{ marginBottom: 12 }}>
              <select
                value={itineraryDestId}
                onChange={e => setItineraryDestId(e.target.value)}
                style={{ fontSize: 13 }}
              >
                {itineraryByDestination.map(({ dest }) => (
                  <option key={dest.destination_id} value={String(dest.destination_id)}>
                    {dest.destination_name}
                  </option>
                ))}
              </select>
            </div>
            {itineraryByDestination
              .filter(({ dest }) => String(dest.destination_id) === itineraryDestId)
              .map(({ dest, days }) => (
                <div key={dest.destination_id} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {days.map(({ dateStr, acts, sunrise, sunset }) => (
                    <div key={dateStr} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tp-text)', marginBottom: 2 }}>
                        {formatWeekday(dateStr)}, {formatDateReadable(dateStr)}
                      </div>
                      {(sunrise || sunset) && (
                        <div style={{ fontSize: 11, color: 'var(--tp-muted)', marginBottom: 4 }}>
                          {[sunrise && `↑ ${sunrise}`, sunset && `↓ ${sunset}`].filter(Boolean).join('  ·  ')}
                        </div>
                      )}
                      <DroppableDayBox destId={dest.destination_id} dateStr={dateStr}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {acts.length === 0 ? (
                            <div style={{ fontSize: 11, color: 'var(--tp-muted)', fontStyle: 'italic' }}>
                              Drop here
                            </div>
                          ) : (
                            acts.map(a => {
                              const tz = dest.timezone || 'UTC';
                              const start = formatTime24(a.start_time, tz);
                              const end = formatTime24(a.end_time, tz);
                              const timeStr = [start, end].filter(Boolean).join(' – ') || '—';
                              return (
                                <DraggableActivityBlock key={a.activity_id} activity={a} dest={dest}>
                                  <div style={{ fontWeight: 500 }}>{a.title || '—'}</div>
                                  <div style={{ color: 'var(--tp-muted)', marginTop: 2 }}>{timeStr}</div>
                                </DraggableActivityBlock>
                              );
                            })
                          )}
                        </div>
                      </DroppableDayBox>
                    </div>
                  ))}
                </div>
              ))}
          </>
        ) : (
          (isDestinationView
            ? itineraryByDestination.filter(({ dest }) => String(dest.destination_id) === itineraryDestId)
            : itineraryByDestination
          ).map(({ dest, days }) => (
            <div key={dest.destination_id} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {days.map(({ dateStr, acts, sunrise, sunset }) => (
                <div key={dateStr} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tp-text)', marginBottom: 2 }}>
                    {formatWeekday(dateStr)}, {formatDateReadable(dateStr)}
                  </div>
                  {(sunrise || sunset) && (
                    <div style={{ fontSize: 11, color: 'var(--tp-muted)', marginBottom: 4 }}>
                      {[sunrise && `↑ ${sunrise}`, sunset && `↓ ${sunset}`].filter(Boolean).join('  ·  ')}
                    </div>
                  )}
                  <DroppableDayBox destId={dest.destination_id} dateStr={dateStr}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {acts.length === 0 ? (
                        <div style={{ fontSize: 11, color: 'var(--tp-muted)', fontStyle: 'italic' }}>Drop here</div>
                      ) : (
                        acts.map(a => {
                          const tz = dest.timezone || 'UTC';
                          const start = formatTime24(a.start_time, tz);
                          const end = formatTime24(a.end_time, tz);
                          const timeStr = [start, end].filter(Boolean).join(' – ') || '—';
                          return (
                            <DraggableActivityBlock key={a.activity_id} activity={a} dest={dest}>
                              <div style={{ fontWeight: 500 }}>{a.title || '—'}</div>
                              <div style={{ color: 'var(--tp-muted)', marginTop: 2 }}>{timeStr}</div>
                            </DraggableActivityBlock>
                          );
                        })
                      )}
                    </div>
                  </DroppableDayBox>
                </div>
              ))}
            </div>
          ))
        )}
        {itineraryByDestination.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--tp-border)' }}>
            <Link to={`/itinerary/${tripId}`} className="tp-btn--ghost" style={{ fontSize: 12 }}>
              View full itinerary →
            </Link>
          </div>
        )}
      </aside>

      {isAdding && (
        <div
          className="tp-modal-backdrop"
          onClick={() => setIsAdding(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Add new idea"
        >
          <div
            className="tp-modal"
            style={{ maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0' }}>Add idea</h3>
            <div className="tp-field">
              <span className="tp-label">Title *</span>
              <input
                type="text"
                placeholder="e.g. Sagrada Familia"
                value={newDraft.title}
                onChange={e => setNewDraft(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="tp-field">
              <span className="tp-label">Destination</span>
              <select
                value={newDraft.destination_id || ''}
                onChange={e => setNewDraft(prev => ({ ...prev, destination_id: e.target.value }))}
              >
                <option value="">Unassigned</option>
                {(destinations || []).map(d => (
                  <option key={d.destination_id} value={String(d.destination_id)}>
                    {d.destination_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="tp-field">
              <span className="tp-label">Description</span>
              <textarea
                placeholder="Notes or details. Supports **markdown**: bold, lists, links, etc."
                value={newDraft.description}
                onChange={e => setNewDraft(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="tp-row" style={{ gap: 16 }}>
              <div className="tp-field" style={{ flex: 1 }}>
                <span className="tp-label">Start time (optional)</span>
                <input
                  type="time"
                  value={newDraft.start_time}
                  onChange={e => setNewDraft(prev => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
              <div className="tp-field" style={{ flex: 1 }}>
                <span className="tp-label">End time (optional)</span>
                <input
                  type="time"
                  value={newDraft.end_time}
                  onChange={e => setNewDraft(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>
            <ActivityTypeSelect
              categories={activityCategories}
              types={activityTypes}
              categoryId={newDraft.category_id}
              typeId={newDraft.type_id}
              onCategoryChange={v => setNewDraft(prev => ({ ...prev, category_id: v, type_id: '' }))}
              onTypeChange={v => setNewDraft(prev => ({ ...prev, type_id: v }))}
              onTypeCreated={newType => {
                loadData();
                setNewDraft(prev => ({ ...prev, type_id: newType.type_id }));
              }}
              placeholderCategory="(None)"
              placeholderType="(None)"
            />
            <div className="tp-field">
              <span className="tp-label">Address / Location</span>
              <AddressSuggestInput
                value={newDraft.address}
                onChange={val => {
                  setNewDraft(prev => ({ ...prev, address: val }));
                  setAddressPlace(null);
                }}
                onSelect={place => {
                  setAddressPlace(place);
                  setNewDraft(prev => ({ ...prev, address: place?.display_name || '' }));
                }}
                placeholder="Optional address"
              />
            </div>
            <div className="tp-field">
              <span className="tp-label">Research link (URL)</span>
              <input
                type="url"
                placeholder="https://..."
                value={newDraft.url}
                onChange={e => setNewDraft(prev => ({ ...prev, url: e.target.value }))}
              />
            </div>
            <div className="tp-field">
              <span className="tp-label">Notes</span>
              <textarea
                placeholder="Extra notes. Supports **markdown**: bold, links, lists, etc."
                value={newDraft.notes}
                onChange={e => setNewDraft(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="tp-row" style={{ marginTop: 12 }}>
              <button type="button" onClick={addItem}>Add</button>
              <button type="button" className="tp-btn--ghost" onClick={() => setIsAdding(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editingId && (
        <div
          className="tp-modal-backdrop"
          onClick={() => setEditingId(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Edit research item"
        >
          <div
            className="tp-modal"
            style={{ maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0' }}>Edit item</h3>
            <div className="tp-field">
              <span className="tp-label">Title *</span>
              <input
                value={editDraft.title}
                onChange={e => setEditDraft(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="tp-field">
              <span className="tp-label">Destination</span>
              <select
                value={editDraft.destination_id || ''}
                onChange={e => setEditDraft(prev => ({ ...prev, destination_id: e.target.value }))}
              >
                <option value="">Unassigned</option>
                {(destinations || []).map(d => (
                  <option key={d.destination_id} value={String(d.destination_id)}>
                    {d.destination_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="tp-field">
              <span className="tp-label">Description</span>
              <textarea
                placeholder="Supports **markdown**"
                value={editDraft.description}
                onChange={e => setEditDraft(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="tp-row" style={{ gap: 16 }}>
              <div className="tp-field" style={{ flex: 1 }}>
                <span className="tp-label">Start time (optional)</span>
                <input
                  type="time"
                  value={editDraft.start_time || ''}
                  onChange={e => setEditDraft(prev => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
              <div className="tp-field" style={{ flex: 1 }}>
                <span className="tp-label">End time (optional)</span>
                <input
                  type="time"
                  value={editDraft.end_time || ''}
                  onChange={e => setEditDraft(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>
            <ActivityTypeSelect
              categories={activityCategories}
              types={activityTypes}
              categoryId={editDraft.category_id}
              typeId={editDraft.type_id}
              onCategoryChange={v => setEditDraft(prev => ({ ...prev, category_id: v, type_id: '' }))}
              onTypeChange={v => setEditDraft(prev => ({ ...prev, type_id: v }))}
              onTypeCreated={newType => {
                loadData();
                setEditDraft(prev => ({ ...prev, type_id: newType.type_id }));
              }}
              placeholderCategory="(None)"
              placeholderType="(None)"
            />
            <div className="tp-field">
              <span className="tp-label">Address</span>
              <AddressSuggestInput
                value={editDraft.address}
                onChange={val => {
                  setEditDraft(prev => ({ ...prev, address: val }));
                  setEditAddressPlace(null);
                }}
                onSelect={place => {
                  setEditAddressPlace(place);
                  setEditDraft(prev => ({ ...prev, address: place?.display_name || '' }));
                }}
              />
            </div>
            <div className="tp-field">
              <span className="tp-label">URL</span>
              <input
                type="url"
                value={editDraft.url}
                onChange={e => setEditDraft(prev => ({ ...prev, url: e.target.value }))}
              />
            </div>
            <div className="tp-field">
              <span className="tp-label">Notes</span>
              <textarea
                placeholder="Extra notes. Supports **markdown**: bold, links, lists, etc."
                value={editDraft.notes}
                onChange={e => setEditDraft(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="tp-row" style={{ marginTop: 12, gap: 8 }}>
              <button type="button" onClick={() => updateItem(editingId)}>Save</button>
              <button type="button" className="tp-btn--ghost" onClick={() => setEditingId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {promoteId && (
        <div
          className="tp-modal-backdrop"
          onClick={() => !isPromoting && setPromoteId(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="tp-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>Promote to Itinerary</h3>
            <p style={{ color: 'var(--tp-muted)', margin: '0 0 16px 0', fontSize: 14 }}>
              Choose where and when to add this activity.
            </p>
            <div className="tp-field">
              <span className="tp-label">Destination</span>
              <select
                value={promoteDest}
                onChange={e => {
                  setPromoteDest(e.target.value);
                  setPromoteDate('');
                }}
              >
                <option value="">Select destination</option>
                {(destinations || []).map(d => (
                  <option key={d.destination_id} value={String(d.destination_id)}>{d.destination_name}</option>
                ))}
              </select>
            </div>
            <div className="tp-field">
              <span className="tp-label">Date</span>
              <select
                value={promoteDate}
                onChange={e => setPromoteDate(e.target.value)}
                disabled={!promoteDest}
              >
                <option value="">Select date</option>
                {promoteDates.map(d => (
                  <option key={d} value={d}>{formatDateReadable(d)}</option>
                ))}
              </select>
            </div>
            <div className="tp-field">
              <span className="tp-label">Start time (optional)</span>
              <input
                type="time"
                value={promoteTime}
                onChange={e => setPromoteTime(e.target.value)}
              />
            </div>
            <div className="tp-row" style={{ marginTop: 16, gap: 8 }}>
              <button type="button" onClick={promoteItem} disabled={isPromoting}>
                {isPromoting ? 'Promoting…' : 'Promote'}
              </button>
              <button type="button" className="tp-btn--ghost" onClick={() => setPromoteId(null)} disabled={isPromoting}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {unescoSuggestion && (
        <UnescoSuggestionBanner
          suggestion={unescoSuggestion.suggestion}
          onConfirm={async () => {
            const labelsRes = await fetch(apiUrl('/api/labels'));
            const labels = await labelsRes.json().catch(() => []);
            const unescoLabel = labels.find(l => l.name === 'UNESCO World Heritage Site');
            if (unescoLabel) {
              await fetch(apiUrl(`/api/wishlist/${unescoSuggestion.ideaId}/labels`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ label_id: unescoLabel.label_id }),
              });
            }
            setUnescoSuggestion(null);
          }}
          onDismiss={() => setUnescoSuggestion(null)}
        />
      )}
    </div>
    </DndContext>
  );
}

export default Wishlist;
